/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import moment from 'moment';
import { map, takeUntil, tap } from 'rxjs/operators';
import { combineLatest, Observable, Subject } from 'rxjs';
import { Ticket, TicketFields } from '@touchplan/common';
import { TicketListVirtual } from './ticket-list-virtual';
import { IWindowTicketData, TicketIndexQuery } from './ticket-index-query';
import { AbstractTicketIndexQuery } from './index-query/abstract-ticket-index-query';
import { QueryFactory } from './query-factory';
import { TicketIndexLogger } from './ticket-index-logger.service';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';
import { DynamicFlagTypes } from '@touchplan/feature-flags';
import { dateCache } from '@touchplan/common';

/**
 */
export class VirtualListManager {
	/** @deprecated */
	public listOfLoadedTickets: TicketListVirtual;
	/** @deprecated */
	public listOfLoadedTicket$: Observable<Ticket[]>;

	// public totalNumberOfTicket$: Observable<number>;

	// MAYBE LATER
	// public firstTicket$: Observable<SnapshotAction<TicketFields>>;
	// MAYBE LATER
	// public lastTicket$: Observable<SnapshotAction<TicketFields>>;

	public ticketIndexQuery: TicketIndexQuery;

	private querie$: Subject<AbstractTicketIndexQuery>;
	private windowCleanupRequest$: Subject<(window: IWindowTicketData[]) => IWindowTicketData[]>;

	// MAYBE LATER
	// private firstTicketQuery: TicketIndexQuery;
	// MAYBE LATER
	// private lastTicketQuery: TicketIndexQuery;

	private shutdownSubject = new Subject<void>();
	private isDestroyed = false;

	constructor(
		private queryFactory: QueryFactory,
		private featureFlagManager: FeatureFlagManager,
		private logger: TicketIndexLogger
	) {
		/*
			MAYBE LATER

		this.firstTicketQuery = this.queryFactory.newTicketIndexQuery();
		this.lastTicketQuery = this.queryFactory.newTicketIndexQuery();
		*/
	}

	public startTicketIndexQuery(projectId: string, planId: string) {
		// Create he main TicketIndexQuery and the Observables that "feed" it:
		this.ticketIndexQuery = this.queryFactory.newTicketIndexQuery();
		this.querie$ = new Subject<AbstractTicketIndexQuery>();
		this.windowCleanupRequest$ = new Subject<(window: IWindowTicketData[]) => IWindowTicketData[]>();

		// Start the main TicketIndexQuery, preparing it to deal with the given project and plan:
		this.ticketIndexQuery.startTicketSnapshotChanges(
			projectId,
			planId,
			this.querie$,
			this.windowCleanupRequest$
		);

		// this.logger.logTicketIndexQueryOutput(this.ticketIndexQuery, false);

		// Have it query centered around today, with the number of days before and after coming from
		// feature-flag observables:
		const today = dateCache.today.format('YYYY-MM-DD');
		combineLatest([
			this.featureFlagManager.getFlag$(DynamicFlagTypes.TICKET_LIST_LOAD_BEFORE_TODAY),
			this.featureFlagManager.getFlag$(DynamicFlagTypes.TICKET_LIST_LOAD_AFTER_TODAY),
		])
			.pipe(
				tap(() => {
					this.windowCleanupRequest$.next(
						// Remove everything from the previous query:
						(ticketWindow: IWindowTicketData[]) => ticketWindow
					);
				}),
				map((pair) => {
					let numberToLoadBeforeToday = pair[0] as number;
					let numberToLoadAfterToday = pair[1] as number;
					// Make sure the numbers are integers that are at least 1 or greater:
					numberToLoadBeforeToday = Math.max(1, Math.round(numberToLoadBeforeToday));
					numberToLoadAfterToday = Math.max(1, Math.round(numberToLoadAfterToday));
					return this.queryFactory.newDateCentricIndexQuery(
						'keyStart',
						today,
						numberToLoadBeforeToday,
						numberToLoadAfterToday
					);
				}),
				takeUntil(this.shutdownSubject)
			)
			.subscribe(this.querie$);
	}

	/*
		MAYBE LATER
	public loadInitialData(
		angularLand: AngularLandConnection,
		projectId: string,
		planId: string
	) {
		this.startTicketIndexQuery(projectId, planId);

		// deprecated
		// this.listOfLoadedTickets = new TicketListVirtual(angularLand, this.ticketIndexQuery.ticketSnapshotChange$);
		// this.listOfLoadedTicket$ = this.listOfLoadedTickets.ticketList$;

		// Keep track of the first and last tickets:
		// this.trackFirstAndLastTickets(projectId, planId);

		// Keeps track of (well) the total number of tickets (and not just the ones we've loaded).
		// In the future, we may just load a count that's stored in Firebase and which is
		// automatically updated by the Backend:

		MAYBE LATER
		Actually, this is inefficient anyway.
		For a 2304 ticket plan, it increases the "load everything" time from 8-9 seconds to 12-15 seconds.
		So if we really need the full count, we should have the Backend maintain that for us;
		DLL said that wouldn't be hard.

		this.trackTotalNumberOfTickets(angularLand.angularFireDatabase, projectId, planId);
	}
	*/

	/*
		MAYBE LATER
	private trackFirstAndLastTickets(
		projectId: string,
		planId: string
	) {
		const firstTicketQuerie$ = new Subject<AbstractTicketIndexQuery>();
		const lastTicketQuerie$ = new Subject<AbstractTicketIndexQuery>();

		this.firstTicketQuery.startTicketSnapshotChanges(
			projectId, planId, firstTicketQuerie$, EMPTY
		);
		this.lastTicketQuery.startTicketSnapshotChanges(
			projectId, planId, lastTicketQuerie$, EMPTY
		);

		firstTicketQuerie$.next(
			this.queryFactory.newSimpleIndexQuery(
				(ref: firebase.database.Reference): firebase.database.Query => {
					return ref.orderByChild('keyStart').startAt('1900-01-01').limitToFirst(1);
				}
			)
		);
		lastTicketQuerie$.next(
			this.queryFactory.newSimpleIndexQuery(
				(ref: firebase.database.Reference): firebase.database.Query => {
					return ref.orderByChild('keyStart').endAt('99999-01-01').limitToLast(1);
				}
			)
		);

		this.firstTicket$ = this.firstTicketQuery.ticketSnapshotChange$.pipe(
			this.childAddedOrRemoved(),
			takeUntil(this.shutdownSubject)
		);
		this.lastTicket$ = this.lastTicketQuery.ticketSnapshotChange$.pipe(
			this.childAddedOrRemoved(),
			takeUntil(this.shutdownSubject)
		);
	}

	private childAddedOrRemoved() {
		return filter((snapshotAction: SnapshotAction<TicketFields>) => (snapshotAction.type === 'child_added') || (snapshotAction.type === 'child_removed'));
	}
	*/

	/*
	private trackTotalNumberOfTickets(
		db: AngularFireDatabase,
		projectId: string,
		planId: string
	) {
		this.totalNumberOfTicket$ = ticketIndexQuerySnapshot$(db, projectId, planId, null).pipe(
			this.childAddedOrRemoved(),
			// Convert the snapshot to a +/- 1 delta, and accumulate it:
			scan((total, snapshot) => total + (snapshot.type === 'child_added' ? 1 : (snapshot.type === 'child_removed' ? -1 : 0)), 0)
		);
	}
	*/

	public stopTicketIndexQuery() {
		if (this.isDestroyed) {
			console.log('WARN - tried to destroy a destroyed VirtualListManager', this);
			return;
		}
		this.shutdownSubject.next();
		this.shutdownSubject.complete();
		this.isDestroyed = true;
	}
}
